import { initObservability } from './observability'
import * as amplitude from '@amplitude/analytics-browser'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import * as React from 'react' // needed for bugsnag
import { createRoot } from 'react-dom/client'
import { HashRouter } from 'react-router-dom'

import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import './index.css'
import '@dialogueconsulting/sked-preview/dist/style.css'

// Gotham (headings only) is loaded async (subset woff2, font-display:swap) so
// it stays out of the critical path. Headings render in Inter until it lands.
import('./font.css').catch(() => {
  /* Gotham is optional — Inter fallback is fine */
})
import Homepage from './Homepage'
import { cleanGoogleTrackingParams } from './utils/cleanGoogleTrackingParams'
import { ensureError } from './utils/ensureError'
import { isObservabilityNoise } from './utils/isObservabilityNoise'
import { mswDomainWhitelist } from './utils/mswWhitelist'

// Initialize observability (OTel traces + OpenObserve RUM/Logs)
initObservability()

// Clean Google tracking parameters before app initialization
cleanGoogleTrackingParams()

const rootElement = document.getElementById('root')
const root = createRoot(rootElement!)
const APP_VERSION = import.meta.env.VITE_APP_VERSION || 'unknown'

if (import.meta.env.MODE === 'test' || import.meta.env.MODE === 'mock') {
  console.log('Starting in test mode with mocks')
  const { worker } = await import('../mocks/browser')

  await worker.start({
    onUnhandledRequest(request, print) {
      //const url = new URL(request.url)

      // Ignore requests to fetch static assets.
      if (mswDomainWhitelist.some((domain) => request.url.includes(domain))) {
        return
      }

      // Otherwise, print a warning for any unhandled request.
      print.warning()
      // or bypass the url completely and send it to the app
      //return
    },
  })

  root.render(
    <HashRouter>
      <Homepage />
    </HashRouter>
  )
} else if (import.meta.env.MODE === 'production') {
  // Keys read from build-time env so they can be rotated without a code
  // change. Fall back to the historical hard-coded values if the env is
  // unset, so the production build behaviour is unchanged on day one;
  // remove the fallback once the env is wired in CI.
  const amplitudeKey =
    import.meta.env.VITE_AMPLITUDE_KEY || 'b9fffa187362da24a877793990facd0e'
  const bugsnagKey =
    import.meta.env.VITE_BUGSNAG_KEY || '6f94020a60e1c9623e2fb1ef4d743676'

  amplitude.init(amplitudeKey, {
    autocapture: true,
  })

  Bugsnag.start({
    apiKey: bugsnagKey,
    appVersion: APP_VERSION,
    releaseStage: 'production',
    plugins: [new BugsnagPluginReact()],
    enabledErrorTypes: {
      unhandledExceptions: true,
      unhandledRejections: false, // We handle this manually below
    },
    onError: function (event) {
      // Safety net: Filter out non-Error objects that might still be caught
      if (event.originalError && !(event.originalError instanceof Error)) {
        // Convert to proper Error object
        const originalError = event.originalError
        event.errors[0].errorMessage =
          typeof originalError === 'string'
            ? originalError
            : JSON.stringify(originalError)
        // Add metadata about the non-Error object
        event.addMetadata('nonErrorObject', {
          type: typeof originalError,
          value: originalError,
        })
      }

      if (
        isObservabilityNoise(
          event.errors[0]?.errorMessage,
          event.errors[0]?.stacktrace
            ?.map((frame) => frame.file || frame.method)
            .join('\n')
        )
      ) {
        return false
      }

      return true
    },
  })

  // Custom unhandled rejection handler to properly handle non-Error rejections
  // This captures rejections before Bugsnag would, allowing us to convert
  // non-Error values (strings, undefined, null, objects) into proper Error
  // objects with stack traces for better debugging in Bugsnag.
  window.onunhandledrejection = (event: PromiseRejectionEvent) => {
    const reason = event.reason

    // Convert to proper Error (if not already) and notify Bugsnag
    const error = ensureError(reason)
    const wasAlreadyError = reason instanceof Error

    if (isObservabilityNoise(error.message, error.stack)) {
      event.preventDefault()
      return
    }

    Bugsnag.notify(error, (bugsnagEvent) => {
      bugsnagEvent.unhandled = true
      bugsnagEvent.severity = 'error'

      // Add metadata about the original rejection for debugging
      if (!wasAlreadyError) {
        bugsnagEvent.addMetadata('unhandledRejection', {
          originalType: reason === null ? 'null' : typeof reason,
          originalValue: reason,
          wasNonError: true,
        })
      }
    })

    // Prevent default browser console error for non-Error rejections
    // (Bugsnag.notify already logs it)
    if (!wasAlreadyError) {
      event.preventDefault()
    }
  }
  // GA3 / Universal Analytics (UA-*) was sunset by Google in July 2023:
  // ReactGA.initialize/pageview no longer reach a live property. Removed
  // entirely. Amplitude (above) is the live analytics layer; if a GA4
  // property is wired up later, switch to `react-ga4` and add it here.

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const ErrorBoundary = Bugsnag.getPlugin('react')!.createErrorBoundary(
    React as any
  )

  root.render(
    <ErrorBoundary>
      <HashRouter>
        <Homepage />
      </HashRouter>
    </ErrorBoundary>
  )
} else {
  // include amplitude in staging
  if (!window.location.hostname.includes('localhost')) {
    amplitude.init('b9fffa187362da24a877793990facd0e', {
      autocapture: true,
    })
  }

  root.render(
    <HashRouter>
      <Homepage />
    </HashRouter>
  )
}
